/**
 * DulhaDulhun – Admin Axios Client
 *
 * In development, always use a relative `/api` base URL so every request is
 * forwarded through the Vite dev-server proxy.  This avoids CORS entirely
 * because the browser sees requests going to the same origin (localhost:5174)
 * rather than directly to the backend origin.
 *
 * In production (built bundle) use the full VITE_API_URL env var.
 */
import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import { ADMIN_CONFIG } from '../config';

// ─── Resolve base URL ─────────────────────────────────────────────────────────
// DEV  → relative path → Vite proxy handles it → no CORS issues
// PROD → full absolute URL from env
const BASE_URL: string = import.meta.env.DEV
  ? '/api'
  : (import.meta.env.VITE_API_URL as string | undefined) || 'http://localhost:5001/api';

// ─── Admin API instance ────────────────────────────────────────────────────────
export const adminApi: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: 15_000,
  headers: { 'Content-Type': 'application/json' },
  // Do NOT set withCredentials when using a relative URL through the proxy –
  // the proxy adds auth headers server-side and we use JWT in Authorization anyway.
  withCredentials: false,
});

// ── Attach admin JWT on every request ─────────────────────────────────────────
adminApi.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem(ADMIN_CONFIG.tokenKey);
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// ── Response interceptor: auto-logout on 401 ──────────────────────────────────
adminApi.interceptors.response.use(
  (res: AxiosResponse) => res,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem(ADMIN_CONFIG.tokenKey);
      localStorage.removeItem('dd_admin_info');
      window.dispatchEvent(new CustomEvent('admin:unauthorized'));
    }
    // Surface the most useful error message
    const message =
      error.response?.data?.message ||
      error.response?.data?.error ||
      error.message ||
      'Request failed';
    return Promise.reject(Object.assign(error, { message }));
  },
);

export default adminApi;
